import { cn } from "@/lib/utils"
import { Check, AlertCircle, Clock } from "lucide-react"

export interface TimelineEvent {
  id: string
  step: string
  tag: "shop" | "etic" | "sup" | "ce" | "sys"
  title: string
  description?: string | null
  occurred_at?: string | null
  state?: "done" | "current" | "waiting" | "error"
}

const tagStyles: Record<TimelineEvent["tag"], string> = {
  shop: "bg-violet-50 text-violet-700",
  etic: "bg-sky-50 text-sky-700",
  sup: "bg-amber-50 text-amber-700",
  ce: "bg-green-50 text-green-700",
  sys: "bg-zinc-100 text-zinc-700",
}

const tagLabels: Record<TimelineEvent["tag"], string> = {
  shop: "Shopify",
  etic: "ETICDATA",
  sup: "Fornecedor",
  ce: "Correos",
  sys: "Sistema",
}

export function Timeline({ events }: { events: TimelineEvent[] }) {
  return (
    <ol className="relative space-y-4 pl-6 before:absolute before:left-[11px] before:top-3 before:bottom-3 before:w-px before:bg-border">
      {events.map((evt) => (
        <li key={evt.id} className="relative">
          <Marker state={evt.state ?? "done"} />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{evt.title}</span>
                <span className={cn("rounded px-1.5 py-0.5 text-[10px] font-mono", tagStyles[evt.tag])}>
                  {tagLabels[evt.tag]}
                </span>
              </div>
              {evt.description && <p className="mt-0.5 text-xs text-muted-foreground">{evt.description}</p>}
            </div>
            {evt.occurred_at && (
              <span className="shrink-0 font-mono text-[11px] text-muted-foreground tabular-nums">
                {formatTime(evt.occurred_at)}
              </span>
            )}
          </div>
        </li>
      ))}
      {events.length === 0 && <li className="text-xs text-muted-foreground">Sem eventos.</li>}
    </ol>
  )
}

function Marker({ state }: { state: "done" | "current" | "waiting" | "error" }) {
  return (
    <span
      className={cn(
        "absolute -left-6 top-0.5 flex size-4 items-center justify-center rounded-full border-2 bg-background",
        state === "done" && "border-foreground bg-foreground text-background",
        state === "current" && "border-[var(--step-current)]",
        state === "waiting" && "border-amber-500 bg-amber-50",
        state === "error" && "border-destructive bg-destructive text-destructive-foreground",
      )}
    >
      {state === "done" && <Check className="size-2.5" strokeWidth={3} />}
      {state === "error" && <AlertCircle className="size-2.5" />}
      {state === "waiting" && <Clock className="size-2.5 text-amber-700" />}
      {state === "current" && <span className="size-1.5 rounded-full bg-[var(--step-current)]" />}
    </span>
  )
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
  } catch {
    return ""
  }
}
